<template>
  <div class="bg-gray-800 border border-gray-600  "
       :class="{ 'host--highlighted': highlighted }">
    <div class="flex flex-col sm:flex-row sm:items-center justify-between mt-4">
      <div class="flex flex-col justify-between min-w-0">
        <div class="flex items-center sm:mr-8">
          <h2 class="relative text-2xl  mr-3">
            <div class="glow-block "
                 :class="[
                     {'glow-block--yellow': findingsCount},
                     {'glow-block--green': !findingsCount}
                   ]"></div>
            <span class="ml-4">{{ ip }}</span>
          </h2>
          <div class="flex uppercase text-xs px-1 font-semibold tracking-wider border
                      leading-relaxed "
               :class="[
                 {'bg-gray-600 text-gray-200 border-gray-600': category === 'external'},
                 // eslint-disable-next-line max-len
                 {'bg-yellow-700 text-gray-200 border-yellow-500 shadow-glowyellow': category === 'internal'},
               ]">
            <span class="block">{{ category }}</span>
          </div>
        </div>

        <div class="ml-4 mt-2 md:mt-0 md:mr-8 ">
          <span class="flag-icon"
                :title="country.toLowerCase()"
                :aria-label="country.toLowerCase()"
                :class="`flag-icon-${country.toLowerCase()}`"
                @mouseover="showTooltip = true" @mouseout="showTooltip = false">
            <span v-if="showTooltip" class="tooltip">
              {{ country }}
            </span>
          </span>
        </div>
        <div class="ml-4 mt-2 lg:mt-0 lg:mr-8">
          <span v-if="asn">
            {{ asn }}
          </span>
          <span v-else
                class="italic text-gray-300">
            No ASN
          </span>
        </div>
        <div class="ml-4 mt-2 lg:mt-0 lg:mr-8">
          <span v-if="org">
            {{ org }}
          </span>
          <span v-else
                class="italic text-gray-300">
            No Organization
          </span>
        </div>
      </div>
      <div class="flex ml-4 mt-4
                  sm:justify-end sm:ml-0 sm:mt-0 sm:pr-8 flex-shrink-0 ">
        <WarningIcon v-if="findingsCount" class="inline-block mr-1" />
        <span
            class=" "
            :class="[
                {'italic text-gray-300': findingsCount === 0},
              ]">
            {{ findingsCount }} {{ 'Finding' | pluralize(findingsCount) }}
        </span>
      </div>
    </div>
    <div
      v-if="isSuperuser"
      class="block mb-2 pl-5 ">
      <Checkbox
        :id="`${ip}-dir-enum`"
        data-testid="host-checkbox"
        :checked="targetsForDictEnum"
        @change="() => $emit('dirEnumTargetUpdated',  ip)"
        label="Select for directory enumeration"
        :value="ip"/>
    </div>
    <template v-if="expanded">
      <div
          v-if="categoryExplanation"
          class="border-t border-gray-600 pt-2 px-2">
        <p class="text-sm mb-2 bg-gray-900 text-gray-200 border border-gray-600 p-2">
          <pre class="break-all whitespace-pre-wrap">{{ categoryExplanation }}</pre>
        </p>
      </div>
      <div v-if="findingsCount" class="border-t border-gray-600 p-2">
        <div class="relative bg-gray-900 pb-2 pt-3
             ">
          <div class="warning-strip "></div>
          <div class="flex mb-2">
            <WarningIcon class="mx-2" />
            <span class="uppercase relative inline-block">
              Findings [{{ findingsCount }}]
            </span>
          </div>
          <ul v-if="Object.keys(findings).length">
            <li v-for="(finding, index) in findings"
                class="yellow-block flex relative pl-3 mb-2 pr-2"
                :key="`finding-${index}`">
              <span>{{ finding }}</span>
            </li>
          </ul>
        </div>
      </div>
      <div class="border-t border-gray-600 p-2">
        <div class="flex flex-col mb-1">
            <span class="text-lg ">

              {{ 'Subdomain' | pluralize(subdomains.length) }} [{{ subdomains.length }}]
            </span>
        </div>
        <div
          v-if="subdomains.length > 0"
          class="flex flex-col lg:flex-row">
          <div
            :class="{ 'px-2' : screenshots.length > 0 }"
            class="lg:w-1/4 flex-grow ">
            <div
              class="uppercase text-gray-300 text-xs font-semibold tracking-widest mb-2">
              Subdomains
            </div>
            <ul class="  ">
              <li
                v-for="(subdomain, index) in subdomains"
                  :key="`${ip}-subdomain-${index}`"
              >
                <Checkbox
                  v-if="isSuperuser"
                  :id="`${ip}-${subdomain}_dict-enum`"
                  data-testid="subdomain-checkbox"
                  :checked="targetsForDictEnum"
                  @change="() => $emit('dirEnumTargetUpdated',  `${subdomain}.${rootDomain}`)"
                  :label="subdomain"
                  :value="`${subdomain}.${rootDomain}`"/>
                <div v-else>{{ subdomain }}</div>
              </li>
            </ul>
          </div>
          <div
            class='lg:w-3/4'
            v-if="screenshots.length > 0">
            <div class="uppercase text-gray-300 text-xs font-semibold tracking-widest mb-2">
              Screenshots
            </div>
            <ul
              class='grid grid-cols-2 gap-2 mb-2 lg:grid-cols-3 pr-2'>
              <li v-for="(screenshot, index) in screenshots"
                  class="flex flex-col uppercase px-1 pt-1 pb-2 bg-black font-semibold"
                  :key="`${ip}-subdomain-${index}`">
                <div
                  v-if="screenshot.file">
                  <a :href="screenshot.file"
                     target="_blank"
                     rel="noopener noreferrer">
                    <img :src="screenshot.file" loading="lazy"/>
                  </a>
                </div>
                <div class="text-xs text-gray-200 px-1 pt-2 truncate">
                  <CopySubdomain
                    :subdomain="screenshot.domainName"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
        <p v-else class="italic text-gray-300 leading-5"
           >
          No subdomains found on this host.
        </p>
      </div>
      <div class="flex flex-col lg:flex-row border-t border-b border-gray-600 px-2"
           >
        <div class="lg:w-3/4 lg:border-r border-gray-600 pt-2 pb-4">
          <div class="flex flex-col mb-2">
            <span class="text-lg">
              {{ 'Port' | pluralize(displayPorts.length) }} [{{ displayPorts.length }}]
            </span>
          </div>
          <div v-if="screenshotTargets.length !== 0">
            <ul class="grid gap-2 grid-cols-3">
              <li v-for="(target, index) in screenshotTargets"
                  class="flex flex-col uppercase px-1 pt-1 pb-2 bg-black font-semibold mb-2"
                  :key="`${ip}-screenshot-target-${index}`">
                <div>
                  <a :href="target.primaryScreenshot.file"
                     :title="target.label"
                     target="_blank"
                     rel="noopener noreferrer"
                     class="block min-w-0 border border-gray-800">
                    <img :src="target.primaryScreenshot.file"
                         :alt="screenshotTargetAltText(target)"
                         class="w-full"
                         loading="lazy"/>
                  </a>
                  <button type="button"
                          class="screenshot-target-text text-xs leading-4 text-gray-100
                                 bg-gray-900 border-l border-r border-b border-gray-800
                                 px-1 py-1 normal-case"
                          @click="showTargetScreenshots(target)">
                    <div class="truncate">
                      {{ target.label }}
                    </div>
                    <div class="text-gray-300 truncate">
                      {{ 'Port' | pluralize(target.ports.length) }}:
                      {{ target.ports.join(', ') }}
                    </div>
                    <div v-if="additionalTargetScreenshotCount(target)"
                         class="text-blue-300 hover:text-blue-200">
                        [+{{ additionalTargetScreenshotCount(target) }} more screenshots]
                    </div>
                  </button>
                </div>
                <button type="button"
                        class="screenshot-target-text flex justify-between px-1 pt-1"
                        @click="showTargetScreenshots(target)">
                  <span class="text-white">{{ target.ports[0] }}</span>
                  <span class="flex items-center text-2xs text-gray-200">
                    <template v-if="target.state === 'open'">
                      <div class="rounded-full text-2xs bg-green-500 w-2 h-2 mr-1 shadow-glowgreen">
                      </div>
                    </template>
                    <template v-if="target.state === 'closed'">
                      <div
                        class="rounded-full text-2xs border border-gray-200 bg-black w-2 h-2 mr-1">
                      </div>
                    </template>
                    <div class="leading-3">{{ target.state }}</div>
                  </span>
                </button>
                <button type="button"
                        class="screenshot-target-text text-xs text-gray-200 px-1"
                        @click="showTargetScreenshots(target)">
                  {{ target.protocol }}
                  <template v-if="target.banner">
                    <span class="text-gray-600 mx-1">|</span>
                    <span class="truncate block">
                      {{ target.banner }}
                    </span>
                  </template>
                  <template v-else-if="target.product">
                    <span class="text-gray-600 mx-1">|</span>
                    {{ target.product }}
                  </template>
                </button>
              </li>
            </ul>
          </div>
          <div v-if="networkServiceCount !== 0"
               class="network-services mt-3 pr-2">
            <div class="flex items-end justify-between mb-2">
              <span class="uppercase text-gray-300 text-xs font-semibold tracking-widest">
                Network Services
              </span>
              <span class="uppercase text-gray-300 text-2xs tracking-widest">
                {{ networkServiceCount }} without screenshots
              </span>
            </div>
            <ul v-if="networkPorts.length"
                class="grid gap-2 grid-cols-1 sm:grid-cols-2">
              <li v-for="port in networkPorts"
                  class="min-w-0"
                  :key="`${ip}-network-port-${networkPortKey(port)}`">
                <button type="button"
                        class="network-service-card flex flex-col w-full h-full px-3 py-2
                               bg-black border text-left"
                        :disabled="!networkPortHasDetails(port)"
                        :class="{
                          'network-service-card--selected': isNetworkPortSelected(port),
                          'border-gray-800': !isNetworkPortSelected(port),
                        }"
                        @click="toggleNetworkEvidence(port)">
                  <span class="flex items-center justify-between w-full mb-1">
                    <span class="flex items-baseline min-w-0">
                      <span class="text-xl text-white leading-none">{{ port.portid }}</span>
                      <span class="uppercase text-xs text-gray-300 ml-2">
                        {{ port.protocol }}
                      </span>
                    </span>
                    <span class="flex items-center uppercase text-2xs text-gray-200">
                      <span v-if="port.state === 'open'"
                            class="rounded-full bg-green-500 w-2 h-2 mr-1 shadow-glowgreen">
                      </span>
                      <span v-else
                            class="rounded-full border border-gray-200 bg-black w-2 h-2 mr-1">
                      </span>
                      {{ port.state }}
                    </span>
                  </span>
                  <span class="network-service-name block w-full text-base text-white truncate">
                    {{ networkPortName(port) }}
                    <span v-if="port.version" class="ml-1 text-sm text-gray-200">
                      {{ port.version }}
                    </span>
                  </span>
                  <span v-if="networkPortDescription(port)"
                        class="block w-full text-xs text-gray-300 truncate"
                        :title="networkPortDescription(port)">
                    {{ networkPortDescription(port) }}
                  </span>
                  <span v-if="networkPortHasDetails(port)"
                        class="block w-full mt-2 text-right uppercase text-2xs
                               tracking-widest text-blue-300">
                      {{ isNetworkPortSelected(port) ? 'Hide evidence' : 'View evidence' }}
                  </span>
                </button>
              </li>
            </ul>

            <div v-if="tcpWrappedPorts.length"
                 class="flex items-center justify-between mt-2 px-3 py-2 bg-black
                        border border-gray-800 text-xs min-w-0">
              <span class="uppercase text-gray-200 tracking-widest flex-shrink-0">
                TCP-wrapped ports [{{ tcpWrappedPorts.length }}]
              </span>
              <span class="ml-3 text-gray-300 truncate"
                    :title="tcpWrappedPortList">
                {{ tcpWrappedPortList }}
              </span>
            </div>

            <div v-if="selectedNetworkPort"
                 class="network-evidence-panel mt-2 bg-gray-900 border border-gray-600 p-3">
              <div class="flex items-start justify-between mb-3">
                <div class="min-w-0 pr-4">
                  <div class="uppercase text-xs text-gray-300 tracking-widest">
                    Service Evidence
                  </div>
                  <div class="text-lg text-white truncate">
                    {{ networkPortName(selectedNetworkPort) }}
                    <span v-if="selectedNetworkPort.version" class="ml-1 text-gray-200">
                      {{ selectedNetworkPort.version }}
                    </span>
                    <span class="text-gray-500 mx-1">/</span>
                    Port {{ selectedNetworkPort.portid }}
                  </div>
                </div>
                <button type="button"
                        class="text-gray-300 hover:text-white p-1"
                        aria-label="Close service evidence"
                        @click="clearNetworkEvidenceSelection">
                  <CloseIcon class="w-3 h-3" />
                </button>
              </div>

              <div v-if="networkEvidenceLoading"
                   class="uppercase text-xs text-gray-300 tracking-widest py-4">
                Loading service evidence...
              </div>
              <div v-else-if="selectedNetworkEvidenceUnavailable"
                   class="network-evidence-unavailable text-sm italic text-gray-300 py-2">
                Service details are temporarily unavailable.
              </div>
              <template v-else-if="selectedNetworkEvidence">
                <div v-if="selectedEvidenceScripts.length">
                  <div class="uppercase text-2xs text-gray-300 tracking-widest mb-1">
                    Observed Service Details
                  </div>
                  <div v-for="script in selectedEvidenceScripts"
                       :key="script.id"
                       class="bg-black border border-gray-700 mb-2">
                    <div class="uppercase text-xs text-gray-200 px-3 py-2 border-b border-gray-700">
                      {{ evidenceSourceLabel(script.id) }}
                    </div>
                    <pre class="network-evidence-output text-xs text-gray-100 px-3 py-2
                                whitespace-pre-wrap">{{ script.output }}</pre>
                  </div>
                </div>
                <div v-if="selectedWebEvidence.length">
                  <div class="uppercase text-2xs text-gray-300 tracking-widest mb-1">
                    Observed Web Responses
                  </div>
                  <div v-for="(evidence, index) in selectedWebEvidence"
                       :key="evidence.url || `${selectedNetworkPortKey}-${index}`"
                       class="network-web-evidence bg-black border border-gray-700 mb-2 px-3 py-2">
                    <div v-if="webEvidenceHeading(evidence)"
                         class="text-sm text-white">
                      {{ webEvidenceHeading(evidence) }}
                    </div>
                    <div v-if="evidence.url"
                         class="network-evidence-value text-xs text-gray-200 mt-1">
                      {{ evidence.url }}
                    </div>
                    <div v-if="evidence.server"
                         class="text-xs text-gray-300 mt-1">
                      Server: <span class="text-gray-100">{{ evidence.server }}</span>
                    </div>
                    <div v-if="evidence.location"
                         class="network-evidence-value text-xs text-gray-300 mt-1">
                      Redirect: <span class="text-gray-100">{{ evidence.location }}</span>
                    </div>
                    <div v-for="field in webProtocolDetails(evidence)"
                         :key="field.label"
                         class="network-evidence-value text-xs text-gray-300 mt-1">
                      {{ field.label }}: <span class="text-gray-100">{{ field.value }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="!selectedEvidenceScripts.length
                           && !selectedWebEvidence.length
                           && selectedServiceSummary"
                     class="network-evidence-summary text-sm text-gray-100">
                  {{ selectedServiceSummary.description }}
                </div>
                <div v-if="!selectedEvidenceScripts.length
                           && !selectedWebEvidence.length
                           && !selectedServiceSummary"
                     class="network-evidence-empty text-sm italic text-gray-300">
                  No additional service details available.
                </div>
              </template>
            </div>
          </div>
          <span v-if="criticalPorts.length == 0"
                class="italic text-gray-300">
            No critical port information available.
          </span>
        </div>
        <div class="lg:w-1/4 border-b lg:border-b-0 border-gray-600 p-2 ">
          <div class="flex flex-col mb-2">
            <span class="text-lg">
              {{ 'Technology' | pluralize(technologies.length) }} [{{ technologies.length }}]
            </span>
          </div>
          <ul v-if="technologies.length > 0">
            <li v-for="(technology, index) in technologies"
                class="flex"
                :key="`${ip}-tech-${index}`">
              <template v-if="technology.classification === 'favorable'">
                <FavorableIcon v-tooltip="getVersionTooltip(technology.isUpToDate)"
                  class="mr-1" />
              </template>
              <template v-if="technology.classification === 'unfavorable'">
                <WarningIcon v-tooltip="getVersionTooltip(technology.isUpToDate)"
                  class="mr-1" />
              </template>
              {{ technology.description }}
              <span
                v-if="technology.semanticVersion"
                v-tooltip="getVersionTooltip(technology.isUpToDate)"
                :class="getVersionClass(technology.isUpToDate)"
                class="text-gray-300 text-xs font-semibold
                cursor-default inline-block ml-1 leading-6">
                  v{{ technology.semanticVersion }}
              </span>
            </li>
          </ul>
          <p class="italic text-gray-300 leading-5"
             v-else>
            No relevant technologies found on this host.
          </p>
        </div>
      </div>
      <div class="raw-data p-2 m-2" v-if="isSuperUser && adminViewActive">
      <Button type="text-small"
              class="py-2 text-gray-300 pl-2"
              @onClick="showRawData = !showRawData">
              {{ showOrHideByValue(showRawData) }}
              Raw Data [{{ renderCorrectExpandElement(showRawData) }}]
            </Button>
            <RawJSON
              v-if='showRawData'
              :json="rawData" />
      </div>
    </template>
    <div class="border-t border-gray-600 ">
      <Button type="text-small"
              class="w-full py-2 text-gray-300"
              @onClick="expanded = !expanded">
        {{ expanded ? 'Collapse' : 'Expand' }}
        <template v-slot:iconRight>
          <Chevron class="text-gray-200 transform
                            transition-transform duration-200 linear"
                   :class="{'-rotate-180': expanded}"/>
        </template>
      </Button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import Button from '@/components/Button.vue';
import Checkbox from '@/components/Checkbox.vue';
import Chevron from '@/assets/chevron.svg?inline';
import CloseIcon from '@/assets/cross.svg?inline';
import CopySubdomain from '@/components/Host/CopySubdomain.vue';
import FavorableIcon from '@/assets/favorable.svg?inline';
import RawJSON from '@/components/Admin/RawJSON.vue';
import WarningIcon from '@/assets/warning.svg?inline';
import api from '@/store/api';

export default {
  name: 'Host',
  components: {
    Button,
    Checkbox,
    Chevron,
    CloseIcon,
    CopySubdomain,
    FavorableIcon,
    RawJSON,
    WarningIcon,
  },
  methods: {
    renderCorrectExpandElement(value) {
      return value ? '-' : '+';
    },
    showOrHideByValue(value) {
      return value ? 'Hide' : 'Show';
    },
    getVersionTooltip(version) {
      if (version === null) return '';
      if (version) return 'Up to date';
      return 'Out of date';
    },
    getVersionClass(version) {
      if (version === null) return 'text-gray-300';
      if (version) return 'text-green-500';
      return 'text-yellow-500';
    },
    portIdentityKey(port) {
      return [
        port.portid || '',
        port.protocol || '',
        port.state || '',
        port.product || '',
        port.banner || '',
      ].join('|');
    },
    uniqueScreenshots(screenshots) {
      const seen = new Set();
      return screenshots.filter((screenshot) => {
        const key = screenshot.file
          || `${screenshot.ipAddress || ''}|${screenshot.port || ''}|${screenshot.domainName || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    portScreenshots(port) {
      if (Array.isArray(port.screenshots) && port.screenshots.length) {
        return this.uniqueScreenshots(port.screenshots);
      }

      if (port.screenshot) {
        return [{
          file: port.screenshot,
          ipAddress: this.ip,
          port: port.portid,
        }];
      }

      return [];
    },
    primaryPortScreenshot(port) {
      return this.portScreenshots(port)[0] || null;
    },
    additionalTargetScreenshotCount(target) {
      return Math.max(target.screenshots.length - 1, 0);
    },
    showTargetScreenshots(target) {
      this.$emit('showPortScreenshots', {
        screenshots: target.screenshots,
        hostIp: this.ip,
        targetLabel: target.label,
      });
    },
    portScreenshotLabel(screenshot) {
      return screenshot.domainName || screenshot.ipAddress || 'IP';
    },
    portScreenshotAltText(screenshot, port) {
      return `Screenshot of ${this.portScreenshotLabel(screenshot)}:${screenshot.port || port.portid}`;
    },
    screenshotTargetAltText(target) {
      return `Screenshot of ${target.label}`;
    },
    networkPortKey(port) {
      return `${port.protocol || ''}-${port.portid || ''}`;
    },
    networkPortName(port) {
      if (port.product) return port.product;
      if (port.serviceSummary && port.serviceSummary.name) return port.serviceSummary.name;
      const bannerConfirmsService = port.banner && port.serviceName
        && String(port.banner).toLowerCase().includes(String(port.serviceName).toLowerCase());
      if (port.serviceName && (port.serviceMethod !== 'table' || bannerConfirmsService)) {
        const aliases = { domain: 'DNS' };
        return aliases[String(port.serviceName).toLowerCase()] || port.serviceName;
      }
      if (port.banner) return 'Service response';
      return '';
    },
    networkPortBannerPreview(port) {
      return String(port.banner || '')
        .replace(/\\x(?:0D|0A)/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 160);
    },
    networkPortDescription(port) {
      const serviceName = port.product && port.serviceMethod !== 'table'
        ? port.serviceName : null;
      const serviceDescription = port.serviceSummary && port.serviceSummary.description;
      return [serviceDescription, serviceName, port.extraInfo, this.networkPortBannerPreview(port)]
        .filter((value) => value)
        .join(' / ');
    },
    networkPortHasDisplayContent(port) {
      if (port.product || port.banner || port.hasServiceDetails) return true;
      const serviceName = String(port.serviceName || '').trim().toLowerCase();
      return port.serviceMethod !== 'table'
        && serviceName !== ''
        && serviceName !== 'unknown';
    },
    isNetworkPortSelected(port) {
      return this.selectedNetworkPortKey === this.networkPortKey(port);
    },
    localNetworkEvidenceFor(port) {
      const localPort = {};
      if (port.banner) {
        localPort.nmapEvidence = {
          scripts: [{ id: 'banner', output: port.banner }],
        };
      }
      if (port.serviceSummary && port.serviceSummary.description) {
        localPort.serviceSummary = port.serviceSummary;
      }
      return Object.keys(localPort).length ? { port: localPort } : null;
    },
    networkEvidenceRequestCanBeRetried(error) {
      if (error && error.isSessionExpired) return false;
      if (!error || !error.response) return true;
      const { status } = error.response;
      return status === 408 || status === 429 || status >= 500;
    },
    waitForNetworkEvidenceRetry(attempt) {
      return new Promise((resolve) => {
        setTimeout(resolve, 300 * (2 ** attempt));
      });
    },
    async fetchNetworkEvidence(port, attempts = 3, attempt = 0) {
      try {
        return await api.get(`reports/${this.reportId}/network-evidence/`, {
          params: { ip: this.ip, port: port.portid, protocol: port.protocol },
          incrementRequestCount: false,
          suppressGlobalErrors: true,
        });
      } catch (error) {
        const finalAttempt = attempt === attempts - 1;
        if (finalAttempt || !this.networkEvidenceRequestCanBeRetried(error)) throw error;
        await this.waitForNetworkEvidenceRetry(attempt);
        return this.fetchNetworkEvidence(port, attempts, attempt + 1);
      }
    },
    networkEvidenceFor(port) {
      return this.networkEvidenceByPort[this.networkPortKey(port)]
        || this.localNetworkEvidenceFor(port);
    },
    networkPortHasDetails(port) {
      return Boolean(port.hasServiceDetails || port.banner);
    },
    evidenceScriptHasDetails(script) {
      if (!script || typeof script !== 'object') return false;
      const id = String(script.id || '').trim().toLowerCase();
      const output = String(script.output || '').trim();
      const normalizedOutput = output.toLowerCase();
      if (!id || id === 'http-server-header') return false;
      if (['', '<empty>', 'error'].includes(normalizedOutput)) return false;
      if (normalizedOutput.startsWith('error:')) return false;
      return ![
        'unable to open connection',
        'problem with xml parsing',
        'script execution failed',
      ].some((message) => normalizedOutput.includes(message));
    },
    evidenceScriptValues(script) {
      const data = script && script.data;
      let elements = [];
      if (data && data.elem) {
        elements = Array.isArray(data.elem) ? data.elem : [data.elem];
      }
      const values = elements
        .map((element) => element && element['#text'])
        .filter((value) => value !== null && value !== undefined && String(value).trim())
        .map((value) => String(value).replace(/\s+/g, ' ').trim());
      if (values.length) return values;
      return [String((script && script.output) || '').replace(/\s+/g, ' ').trim()]
        .filter((value) => value);
    },
    webEvidenceHasDetails(evidence) {
      if (!evidence || typeof evidence !== 'object') return false;
      return ['status', 'title', 'server', 'location']
        .some((key) => evidence[key] !== null && evidence[key] !== undefined
          && String(evidence[key]).trim() !== '');
    },
    webEvidenceHeading(evidence) {
      const status = evidence.status !== null && evidence.status !== undefined
        ? `HTTP ${evidence.status}` : '';
      return [status, String(evidence.title || '').trim()]
        .filter((value) => value)
        .join(' / ');
    },
    webProtocolDetails(evidence) {
      return [
        ['Source', 'evidenceSource'],
        ['Transport', 'httpVersion'],
        ['Content type', 'contentType'],
        ['gRPC status', 'grpcStatus'],
        ['gRPC message', 'grpcMessage'],
      ].map(([label, key]) => ({ label, value: evidence[key] }))
        .filter((field) => field.value !== null && field.value !== undefined
          && String(field.value).trim() !== '');
    },
    isTcpWrappedPort(port) {
      return [port.serviceName, port.product]
        .filter((value) => value)
        .some((value) => String(value).toLowerCase() === 'tcpwrapped');
    },
    clearNetworkEvidenceSelection() {
      this.selectedNetworkPortKey = null;
    },
    async toggleNetworkEvidence(port) {
      const key = this.networkPortKey(port);
      if (this.selectedNetworkPortKey === key) {
        this.clearNetworkEvidenceSelection();
        return;
      }

      this.selectedNetworkPortKey = key;
      if (this.networkEvidenceByPort[key]) return;
      if (!port.hasServiceDetails && this.localNetworkEvidenceFor(port)) return;
      this.$delete(this.networkEvidenceErrorsByPort, key);
      if (!this.reportId) {
        this.$set(this.networkEvidenceErrorsByPort, key, true);
        return;
      }

      this.networkEvidenceLoadingKey = key;
      try {
        const response = await this.fetchNetworkEvidence(port);
        this.$set(this.networkEvidenceByPort, key, response.data);
      } catch (error) {
        this.$set(this.networkEvidenceErrorsByPort, key, true);
      } finally {
        if (this.networkEvidenceLoadingKey === key) this.networkEvidenceLoadingKey = null;
      }
    },
    evidenceSourceLabel(source) {
      const labels = {
        'amqp-info': 'AMQP service details',
        banner: 'Service greeting',
        'fingerprint-strings': 'Observed responses',
      };
      return labels[source] || String(source || 'Service details').replace(/[-_]+/g, ' ');
    },
  },
  props: {
    /*
     The IP of the host
     */
    ip: {
      type: String,
      required: true,
    },
    /*
     Whether the Host is External or Internal
     */
    category: {
      type: String,
      required: true,
    },
    /*
    Explanation of the categorization if available
    */
    categoryExplanation: {
      type: String,
      required: false,
    },
    /*
     The country the host is in
     */
    country: {
      type: String,
      required: true,
    },
    /*
     The ASN of the host
     */
    asn: {
      type: [String, Number],
      required: true,
    },
    /*
     The Organization the host belongs to
     */
    org: {
      type: String,
      required: true,
    },
    /*
     The findings for the host
     */
    findings: {
      type: Array,
      required: true,
    },
    /*
     An array of subdomains found for the host
     */
    subdomains: {
      type: Array,
      required: true,
    },
    /*
     An array of technology objects
     */
    technologies: {
      type: Array,
      required: true,
    },
    /*
     An array of port objects
     */
    ports: {
      type: Array,
      required: true,
    },
    /*
     Overrides state of expanded so all hosts can be collapsed / expanded on report
     */
    forceExpand: {
      type: Boolean || null,
    },
    highlighted: {
      type: Boolean,
      default: false,
    },
    /*
     Determines if superuser features are available
     */
    isSuperuser: {
      type: Boolean || null,
    },
    /*
     An array of screenshots from subdomains
     */
    screenshots: {
      type: Array,
      required: true,
    },
    /*
     The root domain (used to create list of subdomains to target for directory bruteforcing)
     */
    rootDomain: {
      type: String,
    },
    reportId: {
      type: [String, Number],
    },
    targetsForDictEnum: {
      type: Array,
    },
    rawData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      expanded: false,
      showRawData: false,
      filteredSubdomains: [],
      showTooltip: false,
      selectedNetworkPortKey: null,
      networkEvidenceLoadingKey: null,
      networkEvidenceByPort: {},
      networkEvidenceErrorsByPort: {},
    };
  },
  mounted() {
    if (this.forceExpand !== null) {
      this.expanded = this.forceExpand;
    }
  },
  computed: {
    ...mapGetters(['isSuperUser', 'adminViewActive']),
    findingsCount() {
      return this.findings.length || 0;
    },
    displayPorts() {
      const portMap = new Map();

      this.ports.forEach((port) => {
        const key = this.portIdentityKey(port);
        if (!portMap.has(key)) {
          portMap.set(key, {
            ...port,
            screenshots: [...this.portScreenshots(port)],
          });
          return;
        }

        const existing = portMap.get(key);
        existing.screenshots = this.uniqueScreenshots([
          ...this.portScreenshots(existing),
          ...this.portScreenshots(port),
        ]);
        existing.screenshot = existing.screenshot || port.screenshot;
      });

      return [...portMap.values()];
    },
    screenshotTargets() {
      const targetMap = new Map();

      this.displayPorts.forEach((port) => {
        this.portScreenshots(port).forEach((screenshot) => {
          const label = this.portScreenshotLabel(screenshot);
          const key = label;
          if (!targetMap.has(key)) {
            targetMap.set(key, {
              label,
              primaryScreenshot: screenshot,
              screenshots: [],
              ports: [],
              protocol: port.protocol,
              state: port.state,
              product: port.product,
              banner: port.banner,
            });
          }

          const target = targetMap.get(key);
          target.screenshots = this.uniqueScreenshots([...target.screenshots, screenshot]);
          const portId = screenshot.port || port.portid;
          if (portId && !target.ports.includes(String(portId))) {
            target.ports.push(String(portId));
          }
        });
      });

      return [...targetMap.values()]
        .map((target) => ({
          ...target,
          ports: target.ports.sort((left, right) => Number(left) - Number(right)),
        }))
        .sort((left, right) => left.label.localeCompare(right.label));
    },
    screenshotPorts() {
      return this.displayPorts.filter((p) => this.portScreenshots(p).length);
    },
    networkPorts() {
      return this.displayPorts.filter((port) => (
        !this.portScreenshots(port).length
        && !this.isTcpWrappedPort(port)
        && this.networkPortHasDisplayContent(port)
      ));
    },
    tcpWrappedPorts() {
      return this.displayPorts.filter((port) => (
        !this.portScreenshots(port).length && this.isTcpWrappedPort(port)
      ));
    },
    tcpWrappedPortList() {
      return this.tcpWrappedPorts
        .map((port) => port.portid)
        .sort((left, right) => Number(left) - Number(right))
        .join(', ');
    },
    networkServiceCount() {
      return this.networkPorts.length + this.tcpWrappedPorts.length;
    },
    criticalPorts() {
      return [...this.screenshotPorts, ...this.networkPorts, ...this.tcpWrappedPorts];
    },
    selectedNetworkPort() {
      return this.networkPorts.find(
        (port) => this.networkPortKey(port) === this.selectedNetworkPortKey,
      ) || null;
    },
    selectedNetworkEvidence() {
      if (!this.selectedNetworkPort) return null;
      return this.networkEvidenceFor(this.selectedNetworkPort);
    },
    networkEvidenceLoading() {
      return this.selectedNetworkPort
        && this.networkEvidenceLoadingKey === this.networkPortKey(this.selectedNetworkPort);
    },
    selectedNmapEvidence() {
      return (this.selectedNetworkEvidence
        && this.selectedNetworkEvidence.port
        && this.selectedNetworkEvidence.port.nmapEvidence) || {};
    },
    selectedEvidenceScripts() {
      const scripts = this.selectedNmapEvidence.scripts || [];
      const entries = Array.isArray(scripts) ? scripts : [scripts];
      const observedValues = new Set();
      return entries
        .filter((script) => this.evidenceScriptHasDetails(script))
        .filter((script) => {
          const values = this.evidenceScriptValues(script);
          if (values.length && values.every((value) => observedValues.has(value))) return false;
          values.forEach((value) => observedValues.add(value));
          return true;
        });
    },
    selectedWebEvidence() {
      const evidence = (this.selectedNetworkEvidence
        && this.selectedNetworkEvidence.port
        && this.selectedNetworkEvidence.port.webEvidence) || [];
      const entries = Array.isArray(evidence) ? evidence : [evidence];
      return entries.filter((item) => this.webEvidenceHasDetails(item));
    },
    selectedServiceSummary() {
      const evidenceSummary = this.selectedNetworkEvidence
        && this.selectedNetworkEvidence.port
        && this.selectedNetworkEvidence.port.serviceSummary;
      return evidenceSummary
        || (this.selectedNetworkPort && this.selectedNetworkPort.serviceSummary)
        || null;
    },
    selectedNetworkEvidenceUnavailable() {
      return Boolean(this.selectedNetworkPort
        && this.networkEvidenceErrorsByPort[this.networkPortKey(this.selectedNetworkPort)]
        && !this.localNetworkEvidenceFor(this.selectedNetworkPort));
    },
  },
  watch: {
    forceExpand(newForceExpand) {
      if (newForceExpand !== null) {
        this.expanded = newForceExpand;
      }
    },
    highlighted(newHighlighted) {
      if (newHighlighted) {
        this.expanded = true;
      }
    },
  },
};
</script>

<style scoped>
.host--highlighted {
  @apply border-yellow-500 shadow-glowyellow;
}

.screenshot-target-text {
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  margin: 0;
  cursor: pointer;
}

.network-service-card {
  min-height: 7rem;
  transition: border-color 150ms ease, background-color 150ms ease;
}

.network-service-card:not(:disabled):hover,
.network-service-card--selected {
  @apply border-yellow-500;
  background-color: rgba(249, 194, 46, 0.04);
}

.network-service-card:disabled {
  cursor: default;
}

.network-service-card--selected {
  @apply shadow-glowyellow;
}

.network-evidence-output {
  max-height: 12rem;
  overflow: auto;
  font-family: monospace;
  line-height: 1.5;
}

.glow-block {
  @apply absolute left-0;
  top: 0rem;
  width: 2px;
  height: 100%;
  content: '';

  &.glow-block--yellow {
     @apply bg-yellow-500 shadow-glowyellow;
   }

  &.glow-block--green {
     @apply bg-green-500 shadow-glowgreen;
   }
}

.warning-strip {
  @apply absolute h-px w-full top-0 left-0;
  background-color: transparent;
  background-repeat: repeat-x;
  background-size: 100% 88px;
  background-position: 0% 0%;
  background-image: repeating-linear-gradient(-50deg,
  transparent,
  transparent 11px,
  rgba(249, 194, 46, 1) 11px,
  rgba(249, 194, 46, 1) 22px);
}

.yellow-block::after {
  @apply absolute left-0 bg-yellow-500;
  top: 0.35rem;
  width: 2px;
  height: .75rem;
  content: '';
}

.tooltip {
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  background-color: #333;
  color: #fff;
  padding: 5px 10px;
  border-radius: 4px;
  margin-left: 5px;
  z-index: 10;
}
</style>
